import { Box } from '@zillow/constellation';
import Head from 'next/head';
import React from 'react';

import FooterTemplate from '../FooterTemplate/FooterTemplate';
import PostGrid from '../PostGrid/PostGrid';
import { buildPaginatedLink } from '../PostsPagination/buildPaginatedLink';

import { PostsTemplateProps } from './PostsTemplate.types';

const SidebarWrapper: React.FC<{
    children: React.ReactNode;
    sticky?: boolean;
    top?: string;
}> = ({ children, sticky = false, top = '$spacing.md' }) => (
    <Box
        sx={{
            ...(sticky
                ? {
                      position: 'sticky',
                      top: top,
                      alignSelf: 'flex-start',
                  }
                : {}),
        }}
    >
        {children}
    </Box>
);

export const PostsTemplate: React.FC<PostsTemplateProps> = ({
    archiveCategory,
    posts: { posts, pages },
    initialPage,
    baseLink,
    sidebar,
    footer,
    hero,
    query,
    incrementPaginationLinks,
    stickySidebarTop,
}) => {
    return (
        <>
            <Head>
                {initialPage < pages && (
                    <link
                        rel="next"
                        href={buildPaginatedLink({
                            baseLink,
                            query,
                            page: initialPage + 1,
                        })}
                    />
                )}
                {initialPage > 1 && (
                    <link
                        rel="prev"
                        href={buildPaginatedLink({
                            baseLink,
                            query,
                            page: initialPage - 1,
                        })}
                    />
                )}
            </Head>
            <Box
                sx={{
                    margin: '0 auto',
                    maxWidth: '$breakpoints.xxl',
                    padding: '$spacing.xl $spacing.sm',
                    paddingTop: '$spacing.md',
                    '@media $lg': {
                        paddingTop: '$spacing.lg',
                    },
                }}
            >
                {hero && (
                    <Box
                        sx={{
                            marginBottom: '$spacing.xs',
                            '@media $md': {
                                marginBottom: '$spacing.md',
                            },
                            '@media $xl': {
                                marginBottom: '$spacing.xl',
                            },
                        }}
                    >
                        {hero}
                    </Box>
                )}

                <Box
                    sx={{
                        '@media $lg': {
                            display: 'flex',
                        },
                    }}
                >
                    <Box
                        sx={{
                            flex: '1 1 0',
                            minWidth: 0,
                            marginBottom: '$spacing.lg',
                        }}
                    >
                        {posts && (
                            <PostGrid
                                incrementPaginationLinks={
                                    incrementPaginationLinks
                                }
                                posts={posts}
                                pages={pages}
                                page={initialPage}
                                baseLink={baseLink}
                                query={query}
                                archiveCategory={archiveCategory}
                            />
                        )}
                    </Box>
                    {sidebar && (
                        <Box
                            sx={{
                                marginTop: '$spacing.xl',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                width: '300px',
                                '@media $md_lte': {
                                    width: '100%',
                                    marginTop: 0,
                                },
                                '@media $lg': {
                                    marginLeft: '$spacing.md',
                                    marginRight: 0,
                                    marginTop: 0,
                                },
                            }}
                        >
                            <SidebarWrapper
                                sticky={true}
                                top={stickySidebarTop}
                            >
                                {sidebar}
                            </SidebarWrapper>
                        </Box>
                    )}
                </Box>
            </Box>
            {footer && <FooterTemplate footer={footer} />}
        </>
    );
};

export default PostsTemplate;
